// Company stores (S3, 2026-10-12) — portal API client for the store pages.
// The API is apps/api/src/routes/portal-company-stores.ts. The store a call
// acts on always comes from the signed-in person's membership on the server;
// nothing here sends a store id.
import { ApiError, api } from "@/lib/api";
import type { PortalUser } from "@/auth/AuthProvider";

// ─── Who is signed in ────────────────────────────────────────────────────

/** A store employee: signed in with `store_member` and no `client` role.
 *  They only ever see the store pages. */
export function isStoreOnlyUser(user: Pick<PortalUser, "roles"> | null | undefined): boolean {
  return !!user && !user.roles.includes("client");
}

/** Where a person lands after signing in. */
export function homeFor(user: Pick<PortalUser, "roles"> | null | undefined): string {
  return isStoreOnlyUser(user) ? "/store" : "/dashboard";
}

/** The plain message an API refusal carries, or a fallback. */
export function apiMessage(err: unknown, fallback = "Something went wrong. Please try again."): string {
  if (err instanceof ApiError) {
    const body = err.body as { error?: unknown; message?: unknown } | null;
    const msg = typeof body?.error === "string" ? body.error : typeof body?.message === "string" ? body.message : null;
    // Short machine codes ("invalid body", "not found") aren't for people.
    if (msg && /\s/.test(msg) && !/^invalid body$/i.test(msg) && !/^not found$/i.test(msg)) return msg;
    if (err.status === 429) return "Too many tries. Please wait a minute and try again.";
  }
  return fallback;
}

// ─── The store (signed in) ───────────────────────────────────────────────

export type PaymentOption = "company_card" | "invoice" | "employee_card";
export type ApprovalRule = "none" | "all" | "over_amount";

export interface StoreLogo {
  url: string | null;
  mimeType: string;
  name: string;
}

/** A mock-up: the rep's approved virtual of the store's logo on the
 *  product, in one colour. A short-lived https link. */
export interface StoreMockup {
  colour: string | null;
  url: string;
}

export interface StoreProduct {
  id: string;
  productId: string;
  name: string;
  photo: string | null;
  colours: string[];
  branding: string | null;
  /** In the store's colour order; empty = none yet (the plain photo shows). */
  mockups?: StoreMockup[];
}

/** The picture to lead with: the first mock-up, else the plain photo. */
export function leadImage(p: Pick<StoreProduct, "photo" | "mockups">): { src: string; mockup: boolean } | null {
  const m = p.mockups?.find((x) => /^https:\/\//.test(x.url));
  if (m) return { src: m.url, mockup: true };
  return p.photo ? { src: p.photo, mockup: false } : null;
}

/** The mock-up for one colour (null = none; never another colour's). */
export function mockupFor(p: Pick<StoreProduct, "mockups">, colour: string | null): StoreMockup | null {
  const k = (c: string | null) => (c ?? "").trim().toLowerCase();
  return p.mockups?.find((m) => /^https:\/\//.test(m.url) && (m.colour === null || k(m.colour) === k(colour))) ?? null;
}

export interface MyStore {
  store: {
    id: string;
    name: string;
    slug: string;
    /** "closed" once its end date has passed (Stephanie 2026-10-22). */
    status: string;
    /** Last day it takes orders (YYYY-MM-DD); null = no end date. */
    endsOn?: string | null;
    /** The store's delivery addresses (Stephanie 2026-10-22); null = each
     *  person types their own. */
    shipToList?: StoreAddress[] | null;
    logo: StoreLogo | null;
    paymentOptions: PaymentOption[];
    /** The store's company (S4 — "Invoice to <company>"). */
    companyName?: string | null;
    /** S4 — the invoice terms, e.g. "net_30". */
    invoiceTerms?: string | null;
    approvalRule: ApprovalRule;
    /** Admin only. */
    approvalThreshold?: number | null;
    /** Admin only. */
    joinDomain?: string | null;
  };
  role: "admin" | "member";
  products: StoreProduct[];
}

/** The signed-in person's store, or null when they have none (404) or
 *  company stores aren't switched on yet (503). */
export async function fetchMyStore(): Promise<MyStore | null> {
  try {
    return await api<MyStore>("/portal/store");
  } catch (err) {
    if (err instanceof ApiError && (err.status === 404 || err.status === 503)) return null;
    throw err;
  }
}

export const MY_STORE_KEY = ["portal-store"] as const;

export async function updateStoreSettings(patch: {
  approvalRule?: ApprovalRule;
  approvalThreshold?: number | null;
  joinDomain?: string | null;
}): Promise<MyStore> {
  return api<MyStore>("/portal/store/settings", { method: "PATCH", body: JSON.stringify(patch) });
}

// ─── People (admin) ──────────────────────────────────────────────────────

export interface StoreMember {
  id: string;
  userId: string;
  role: "admin" | "member";
  name: string;
  email: string;
  invitedAt: string | null;
  joinedAt: string | null;
  state: "active" | "invited";
}

export async function fetchMembers(): Promise<StoreMember[]> {
  const r = await api<{ members: StoreMember[] }>("/portal/store/members");
  return r.members;
}

export type InviteStatus = "invited" | "added" | "already_member" | "refused" | "invalid" | "failed";
export interface InviteResult {
  email: string;
  status: InviteStatus;
  message?: string;
}

export async function inviteMembers(emails: string): Promise<InviteResult[]> {
  const r = await api<{ results: InviteResult[] }>("/portal/store/members", {
    method: "POST",
    body: JSON.stringify({ emails }),
  });
  return r.results;
}

export async function removeMember(memberId: string): Promise<void> {
  await api(`/portal/store/members/${encodeURIComponent(memberId)}`, { method: "DELETE" });
}

/** The invite email again, for someone who hasn't set a password yet. */
export async function resendInvite(memberId: string): Promise<void> {
  await api(`/portal/store/members/${encodeURIComponent(memberId)}/resend`, { method: "POST" });
}

/** One invite result in plain words. */
export function inviteResultText(r: InviteResult): string {
  switch (r.status) {
    case "invited":
      return "Invited. We've emailed them a link to set a password.";
    case "added":
      return "Added. We've emailed them to let them know.";
    case "already_member":
      return "Already in the store.";
    case "invalid":
      return r.message ?? "That isn't an email address.";
    case "refused":
      return r.message ?? "We couldn't add this email. Please ask your rep at other.";
    case "failed":
    default:
      return r.message ?? "We couldn't add this person. Please try again.";
  }
}

// ─── Public: sign-off ────────────────────────────────────────────────────

export interface SignoffView {
  state: "awaiting" | "approved" | "changes_requested";
  store: {
    name: string;
    companyName: string;
    logo: StoreLogo | null;
    paymentOptions?: PaymentOption[];
    invoiceTerms?: string | null;
  };
  contactFirstName: string | null;
  note: string | null;
  products: StoreProduct[];
}

/** Once a store is approved the link answers only this. */
export interface SignoffApprovedStub {
  status: "approved";
  storeName: string;
}

export type SignoffResponse = SignoffView | SignoffApprovedStub;

export function isApprovedStub(v: SignoffResponse): v is SignoffApprovedStub {
  return (v as SignoffApprovedStub).status === "approved" && !("products" in v);
}

export async function fetchSignoff(token: string): Promise<SignoffResponse> {
  return api<SignoffResponse>(`/portal/store-signoff/${encodeURIComponent(token)}`, { skipAuth: true });
}

export async function approveSignoff(token: string): Promise<{ status: string }> {
  return api<{ status: string }>(`/portal/store-signoff/${encodeURIComponent(token)}/approve`, {
    method: "POST",
    skipAuth: true,
  });
}

export async function requestSignoffChanges(token: string, note: string): Promise<{ status: string }> {
  return api<{ status: string }>(`/portal/store-signoff/${encodeURIComponent(token)}/request-changes`, {
    method: "POST",
    body: JSON.stringify({ note }),
    skipAuth: true,
  });
}

// ─── Public: join ────────────────────────────────────────────────────────

export async function joinStore(slug: string, email: string, name: string): Promise<void> {
  await api(`/portal/store/join/${encodeURIComponent(slug)}`, {
    method: "POST",
    body: JSON.stringify(name.trim() ? { email, name: name.trim() } : { email }),
    skipAuth: true,
  });
}

/** The store's real name for the join page; null when the store isn't open
 *  to join (or can't be reached) — the page then uses the link's name. */
export async function fetchJoinInfo(slug: string): Promise<{ storeName: string } | null> {
  try {
    return await api<{ storeName: string }>(`/portal/store/join/${encodeURIComponent(slug)}/info`, { skipAuth: true });
  } catch {
    return null;
  }
}

// ─── Plain words ─────────────────────────────────────────────────────────

export const TERMS_DAYS: Record<string, string> = {
  net_15: "15",
  net_30: "30",
  net_45: "45",
  net_60: "60",
  net_90: "90",
};

/** "net_30" → "Net 30" — the client document's payment-terms labels
 *  (PublicOrderPage TERMS_LABEL), built on TERMS_DAYS. */
export function termsLabel(terms: string | null | undefined): string {
  if (!terms) return "—";
  const days = TERMS_DAYS[terms];
  if (days) return `Net ${days}`;
  if (terms === "credit_card") return "Credit card";
  if (terms === "upfront") return "Paid upfront";
  return terms.replace(/_/g, " ");
}

/** "How people pay", one line per option the store allows. */
export function paymentOptionText(option: PaymentOption, invoiceTerms?: string | null): string {
  switch (option) {
    case "company_card":
      return "Company card: you (or another admin) pay for each order with the company card.";
    case "invoice": {
      const days = invoiceTerms ? TERMS_DAYS[invoiceTerms] : null;
      return `On invoice: orders go on your account and we send you an invoice${days ? `, due in ${days} days` : ""}.`;
    }
    case "employee_card":
      return "Their own card: each person pays for their order themselves.";
  }
}

/** "acme-co" → "Acme Co" — the join page's fallback when it can't get the store name. */
export function nameFromSlug(slug: string): string {
  return slug
    .split("-")
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}

export function formatDate(iso: string | null): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}

/** "October 31, 2026" from YYYY-MM-DD. */
export function storeDateWords(day: string): string {
  const [y, m, d] = day.split("-").map(Number);
  return new Date(Date.UTC(y!, m! - 1, d!)).toLocaleDateString("en-US", { timeZone: "UTC", month: "long", day: "numeric", year: "numeric" });
}

/** Why ordering is off, in plain words — closed (past its end date) or
 *  paused — or null when the store is open. */
export function storeOffMessage(store: Pick<MyStore["store"], "status" | "endsOn">, where: "browse" | "cart"): string | null {
  if (store.status === "closed") return `This store closed${store.endsOn ? ` on ${storeDateWords(store.endsOn)}` : ""}, so ordering is off. Questions? Contact your rep at other.`;
  if (store.status !== "live") return where === "browse" ? "This store is paused for now. You can look, but ordering is off until it reopens." : "This store is paused for now, so ordering is off until it reopens.";
  return null;
}

/** A store delivery address. */
export interface StoreAddress {
  label?: string | null;
  company?: string | null;
  street1: string;
  street2?: string | null;
  city: string;
  stateCode: string;
  postalCode: string;
  residential: boolean;
}

/** "100 Main St, Suite 4, Boca Raton, FL 33431" */
export function storeAddressLine(a: StoreAddress): string {
  return [a.street1, a.street2, `${a.city}, ${a.stateCode} ${a.postalCode}`].filter(Boolean).join(", ");
}
